<template>
  <div class="tab_slide_content">
    <div ref="header" class="main-header">
      <div class="title">
        <span @click="onClickCodingProjects" class="span-project-name">Coding / Projects /</span> <span @click="onClickProjectName" class="span-project-name" >{{ project && (project.project_name + ' / ') }}</span>
        <span class="config-title">Configuration</span>
      </div>
      <div :class="{'disabled': isBusy && projectSetupIsLoading}">
        <tabs
          :tabs="availableTabs"
          :currentTabValue="currentTabValue"
          wrapper-class="default-tabs"
          tab-class="default-tabs__item"
          tab-active-class="default-tabs__item_active"
          line-class="default-tabs__active-line"
        />
      </div>
    </div>

    <div ref="content" class="content" v-if="!projectSetupIsLoading">
      <div v-if="currentTabValue === 'general'">
        <General />
        <Organizations />
        <div class="card mt20">
          <h1>Briefing documents</h1>
          <div class="body">
            <input-file
              :get_files_url="`analysis/${projectUid}/files`"
              :remove_url="`analysis/${projectUid}/files`"
              :upload_url='`analysis/${projectUid}/files/upload`'
              :download_url="`analysis/${projectUid}/files/download`"
            />
          </div>
        </div>
      </div>
      <Categories v-else-if="currentTabValue === 'categories'" />
      <Tags v-else-if="currentTabValue === 'tags'" />
      <AutomatedTags v-else-if="currentTabValue === 'automated-tags'" />
      <Relations v-else-if="currentTabValue === 'relations'"
        ref="relationships"
        :data="relationships"
        :isBusy="relationshipIsBusy"
        :addRelationshipRecord="addRelationshipRecord"
        :updateRelationshipRecord="updateRelationshipRecord"
        :removeRelationshipRecord="removeRelationshipRecord"
        :updateGroupingTags="updateGroupingTags"
        :sortGroupingTable="sortGroupingTable"
        :general="general"
      />
      <Scoring v-else-if="currentTabValue === 'scoring'" />
      <CountriesList v-else-if="currentTabValue === 'households'" />
      <Breakdowns v-else-if="currentTabValue === 'breakdowns'" />
      <AICoding v-else-if="currentTabValue === 'ai_coding'" />
    </div>
  </div>
</template>

<script>

import _ from 'lodash';
import { mapState, mapActions, mapGetters } from 'vuex';

import { SCORING_METHODS } from '../../config/const';
import setMarginTop from '../../helpers/setMarginTop';

import Tabs from '../vue-tabs-with-active-line';
import General from './general';
import Organizations from './organizations';
import Categories from './categories';
import CountriesList from './countries';
import Tags from './tags';
import AutomatedTags from './automated-tags';
import Scoring from './scoring';
import Relations from './relations';
import Breakdowns from './breakdowns';
import AICoding from './ai-coding';
import InputFile from '../form-components/input-file';

export default {
  components: {
    Tabs,
    General,
    Organizations,
    Categories,
    Tags,
    Scoring,
    Relations,
    InputFile,
    CountriesList,
    AutomatedTags,
    Breakdowns,
    AICoding
  },

  props: {
    project: Object
  },

  computed: {
    ...mapState({
      projectSetup: state => state.projectSetup.projectSetup,
      isBusy: state => state.projectSetup.projectSetup.isBusy,
      relationshipIsBusy: state => state.projectSetup.relationships.isBusy,
      general: state => state.projectSetup.projectSetup.data ? state.projectSetup.projectSetup.data.general : {},
      user: state => state.user.user.data,
    }),
    ...mapGetters({
      hasAclPermission: 'acl/hasAclPermission'
    }),
    currentTab() {
      if(this.currentHash) return this.availableTabs.find(tab => tab.value.toLowerCase() === this.currentHash)
      if(this.defaultTab) return this.availableTabs.find(tab => tab.value.toLowerCase() === this.defaultTab)
      return {}
    },
    currentTabValue() { return this.currentTab && this.currentTab.value ? this.currentTab.value : null },
    currentHash() { return this.$route.hash ? this.$route.hash.substring(1) : null },

    relationships() {

      const categories = this.projectSetup.data.categories ? JSON.parse(JSON.stringify(this.projectSetup.data.categories)) : [];
      const groupings = this.projectSetup.data.groupings ? JSON.parse(JSON.stringify(this.projectSetup.data.groupings)) : [];

      return {
        categories,
        groupings
      }
    },

    availableTabs () {
      const tabs = [
        { title: this.$i18n.t('configuration.general'), value: 'general', order: 1 },
        { title: this.$i18n.t('configuration.categories'), value: 'categories', order: 2 },
        { title: this.$i18n.t('configuration.tags'), value: 'tags', order: 3 },
        { title: this.$i18n.t('configuration.relations'), value: 'relations', order: 5 },
        { title: this.$i18n.t('configuration.households'), value: 'households', order: 7 },
        { title: this.$i18n.t('configuration.breakdowns'), value: 'breakdowns', order: 8 }
      ];

      if (this.general?.display_automated_tags) {
        tabs.push({ title: this.$i18n.t('configuration.automated_tags'), value: 'automated-tags', order: 4 });
      }

      if (this.general?.scoring && this.general?.scoring !== SCORING_METHODS.NONE) {
        tabs.push({ title: this.$i18n.t('configuration.scoring'), value: 'scoring', order: 6 });
      }

      if (this.general?.auto_coder && this.hasAclPermission('carma_ai', 'codings')) {
        tabs.push({ title: this.$i18n.t('configuration.ai_coding'), value: 'ai_coding', order: 9 });
      }

      return tabs.sort((a, b) => a.order > b.order ? 1 : -1);
    }
  },
  created() {
    const projectUid = this.projectUid;
    this.projectSetupIsLoading = true;
    this.getprojectSetup({ projectUid }).then(() => {
      this.setDefaultTab();
      this.projectSetupIsLoading = false;
    });
    // Charles: when ACL is more used, this should be handled at root level of this code (router level)
    this.getPermissions();
  },
  mounted ()  {
    this.$nextTick(() => {
      this.setMarginTopMain();
      window.addEventListener('resize', this.setMarginTopMain); 
    });
  },
  beforeDestroy: function(){
    window.removeEventListener('resize', this.setMarginTopMain);
  },
  data: function() {
    const projectUid = this.$route.params.project_uid;
    return {
      defaultTab: 'general',
      projectUid,
      addNewRow: false,
      projectSetupIsLoading: false
    }
  },
  methods: {
    ...mapActions({
      'getprojectSetup': 'projectSetup/getprojectSetup',
      'getCategories': 'projectSetup/getCategories',
      'updateprojectSetup': 'projectSetup/updateprojectSetup',
      'addRelationship': 'projectSetup/addRelationship',
      'updateRelationship': 'projectSetup/updateRelationship',
      'removeRelationship': 'projectSetup/removeRelationship',
      'getGroupingsWithTags': 'projectSetup/getGroupingsWithTags',
      'updateGrouping': 'projectSetup/updateGrouping',
      'getPermissions': 'acl/getPermissions',
    }),

    setDefaultTab() {
      if(!this.currentHash && this.currentTabValue) {
        return this.$router.push({hash: this.currentTabValue})
      }
    },

    setMarginTopMain(e){
      let mainHeader = this.$refs['header'];
      let mainContent = this.$refs['content'];
      mainHeader && mainContent && setMarginTop(mainHeader, mainContent, -30);
    },

    updateGroupingTags(grouping) {
      const projectUid = this.projectUid
      return this.updateGrouping({ projectUid, grouping });
    },

    sortGroupingTable(pagination = null){
      const projectId= this.projectUid;
      return this.getGroupingsWithTags({projectId, pagination});
    },

    addRelationshipRecord(type, relationship) {
      const projectUid = this.projectUid
      return this.addRelationship({ projectUid, type, relationship }).then(() => {
        this.$refs.relationships.closePanel();
      });
    },

    updateRelationshipRecord(type, relationship) {
      const projectUid = this.projectUid
      return this.updateRelationship({ projectUid, type, relationship }).then(() => {
        this.$refs.relationships.closePanel();
      });
    },

    async removeRelationshipRecord(type, relationship) {
      const projectUid = this.projectUid
      return this.removeRelationship({ projectUid, type, relationship }).then(() => {
        this.$refs.relationships.closePanel();
      });
    },

    onClickProjectName: function(id) {
      const projectId = this.$route.params.project_uid;
      const projectUrl = `/analysis/projects/${projectId}`;
      this.$router.push(projectUrl);
    },

    onClickCodingProjects: function(id) {
      const projectId = this.$route.params.project_uid;
      const projectUrl = `/analysis/projects/`;
      this.$router.push(projectUrl);
    }
  },
};
</script>

<style lang="scss" scoped>
  .tab_slide_content {
    padding: 0 !important;
    flex-direction: column;
  }

  .config-title {
    color: #ec9148;
  }

  .span-project-name {
    cursor: pointer;
  }

  .briefings-container {
    position: relative;
    min-height: 60px;
  }

</style>
